import { useNavigate } from "react-router-dom";
import { resolveSelfGuard, shouldBlockSelfAction } from "../self-guard";
import { FoundingPartnerSection } from "@/features/founding/FoundingPartnerSection";
import { useFoundingAccount } from "@/features/founding/use-founding-account";
import { MembershipsSection } from "../memberships/MembershipsSection";
import { useOwnedKinds } from "../memberships/use-owned-kinds";
import { TosAcceptancesSection } from "../tos/TosAcceptancesSection";
import { UserDetailHeader } from "./UserDetailHeader";
import { UserProfileSection } from "./UserProfileSection";
import { UserNotificationSection } from "./UserNotificationSection";
import { UserActionsSection } from "./UserActionsSection";
import { UserTestAccountSection } from "./UserTestAccountSection";
import { UserDangerZoneSection } from "./UserDangerZoneSection";
import { UserDetailDialogs } from "./UserDetailDialogs";
import { useUserDetailDialogs } from "./use-user-detail-dialogs";
import { useUserDetailCapabilities } from "./use-user-detail-capabilities";
import { useUserDetailActions } from "./use-user-detail-actions";
import { UserActionToast } from "./UserActionToast";
import { useUserActionToast } from "./use-user-action-toast";
import type { UserDetail } from "../types";

interface UserDetailBodyProps {
  readonly user: UserDetail;
}

/**
 * Everything the user detail page shows for ONE loaded user. The page renders
 * it with `key={user.id}`, so an in-SPA navigation to another user (a link or
 * browser back/forward) remounts it: open dialogs, the toast, mutation pending
 * flags and every section's local selection (ToS rows, membership picks,
 * founding form) are dropped instead of being re-bound to the next user.
 */
export function UserDetailBody({ user }: UserDetailBodyProps) {
  const navigate = useNavigate();
  const {
    canManage,
    canNotify,
    canVerifyPhone,
    canReadFounding,
    canReadPayouts,
    canReadMemberships,
    canManageMemberships,
    canChangeMemberRoles,
    canManageOfflineAcceptances,
    currentAdminFirebaseUid,
  } = useUserDetailCapabilities();

  const dialogs = useUserDetailDialogs();
  const { toast, setToast } = useUserActionToast();

  const {
    verifyPhonePending,
    passwordResetPending,
    handleVerifyPhone,
    handleSendPasswordReset,
    handleMutationSuccess,
    handleNotificationSent,
  } = useUserDetailActions(user, setToast);

  // Founding Partner badge + section (ADR-0010 §9): only host / venue-owner
  // accounts can be Founding Partners. Eligibility comes from what the account
  // owns (memberships + owned entities), never from the legacy `users.role` label.
  const ownedKinds = useOwnedKinds(user, canReadMemberships);
  const targetFoundingEligible = ownedKinds.venues || ownedKinds.properties;
  const showFounding = Boolean(canReadFounding && targetFoundingEligible);
  const showMemberships = canReadMemberships;
  const foundingAccount = useFoundingAccount(user.id, showFounding);

  // Hide the Deactivate button on 'self' AND 'unverified' (the latter
  // catches a backend that omits `firebaseUid` on the detail payload —
  // the helper treats missing data as fail-safe so a contract drift
  // can't re-enable self-deactivation). The dialog also refuses to
  // submit defensively. See `self-guard.ts` for the rationale.
  const hideDestructiveSelfActions = shouldBlockSelfAction(
    resolveSelfGuard(currentAdminFirebaseUid, user.firebaseUid),
  );

  // Only owners of a venue organization have payouts, so the link is scoped to that.
  const canViewPayouts = canReadPayouts && ownedKinds.venues;

  // ADR-0009. The API sends this (required); an absent flag only happens on a
  // pre-ADR-0009 build, and must read as "real".
  const isTestAccount = user.isTestAccount === true;

  return (
    <div data-testid="user-detail-screen" className="space-y-6">
      <UserDetailHeader
        user={user}
        showFounding={showFounding}
        foundingPartner={foundingAccount.data?.foundingPartner ?? false}
        canViewPayouts={canViewPayouts}
      />

      <UserActionToast toast={toast} />

      <UserProfileSection
        user={user}
        canVerifyPhone={canVerifyPhone}
        verifyPending={verifyPhonePending}
        onVerifyPhone={handleVerifyPhone}
      />

      <TosAcceptancesSection
        userId={user.id}
        userName={user.displayName || user.email}
        inApp={user.tosAcceptances}
        offline={user.tosOfflineAcceptances ?? []}
        canManage={canManageOfflineAcceptances}
      />

      {showFounding && <FoundingPartnerSection userId={user.id} ownedKinds={ownedKinds} />}

      {/* ADR-0017 B4 (generalizes the ADR-0015 D5 staff card). A staff
          account's memberships need the staff read, which is ADMIN + SUPPORT
          on the API, so a FINANCE viewer (who would 403) never triggers it —
          same pattern as the Founding Partner section. */}
      {showMemberships && (
        <MembershipsSection
          user={user}
          canManage={canManageMemberships}
          canChangeRoles={canChangeMemberRoles}
        />
      )}

      {canNotify && (
        <UserNotificationSection
          phone={user.phone}
          readiness={user.notificationReadiness}
          onSend={() => dialogs.openDialog("notify")}
        />
      )}

      {canManage && (
        <>
          <UserActionsSection
            isActive={user.isActive}
            hideDestructiveSelfActions={hideDestructiveSelfActions}
            passwordResetPending={passwordResetPending}
            onDeactivate={() => dialogs.openDialog("deactivate")}
            onReactivate={() => dialogs.openDialog("reactivate")}
            onChangeRole={() => dialogs.openDialog("changeRole")}
            onSendPasswordReset={handleSendPasswordReset}
          />
          <UserTestAccountSection
            isTestAccount={isTestAccount}
            onToggle={() => dialogs.openDialog("testAccount")}
          />
          <UserDangerZoneSection onDelete={() => dialogs.openDialog("delete")} />
        </>
      )}

      <UserDetailDialogs
        user={user}
        currentAdminFirebaseUid={currentAdminFirebaseUid}
        open={dialogs.open}
        onOpenChange={dialogs.setDialogOpen}
        onMutationSuccess={handleMutationSuccess}
        onDeleted={() => {
          // Detail page is dead — row is gone. Bounce back to the list.
          navigate("/users");
        }}
        onNotificationSent={handleNotificationSent}
      />
    </div>
  );
}
